import type { JSX } from 'react'

import { useEffect, useState } from 'react'
import { COLOR } from 'vars/color'
import { FONTS } from 'vars/typography'
import { CounterSC, CounterWrapper, ProgressValueSC } from './Counter.styled'

export interface ICounterProps {
  duration?: number
  mainColor?: COLOR
  strokeColor?: COLOR
  accentColor?: COLOR
  strokeWidth?: number
  textColor?: COLOR
  size?: number
  font?: FONTS
  onCounterEnded?: () => void
}

export const Counter = ({
  duration = 10,
  mainColor = COLOR.SURFACE_OPACITY_DARK_40,
  strokeColor = COLOR.SURFACE_OPACITY_LIGHT_10,
  accentColor = COLOR.ACCENT_TERTIARY,
  textColor = COLOR.TEXT_ICON_CONSTANT_LIGHT,
  strokeWidth = 4,
  size = 104,
  font = FONTS.Headline2,
  onCounterEnded,
}: ICounterProps): JSX.Element => {
  const [timer, setTimer] = useState(duration)

  const timeoutStep = 1000 //1s
  const startTimer = setTimeout(() => setTimer((prev) => --prev), timeoutStep)
  useEffect(() => {
    if (timer === 0) {
      clearTimeout(startTimer)
      onCounterEnded?.()
    }
  }, [timer])

  const viewBox = `0 0 ${size} ${size}`

  const cy = size / 2 //the center of the circle Y
  const cx = size / 2 //the center of the circle X
  const R1 = cx - 2 - strokeWidth // filled circle radius
  const R2 = cx - 4 - strokeWidth // stroke circle radius

  const path = `M ${cx - R1},${cy} a ${R1},${R1} 0 1,0 ${
    R1 * 2
  },0 a ${R1},${R1} 0 1,0  -${R1 * 2},0` //filled circle path

  const path2 = `M ${cx - R2},${cy} a ${R2},${R2} 0 1,0 ${
    R2 * 2
  },0 a ${R2},${R2} 0 1,0  -${R2 * 2},0` //stroke circle path

  const strokeDashArray = 2 * 3.14 * R2

  return timer > -1 ? (
    <CounterWrapper size={size}>
      <CounterSC
        viewBox={viewBox}
        duration={duration}
        fill='none'
        strokeDashArray={strokeDashArray}
      >
        <path d={path} fill={`var(${mainColor})`} />
        <path
          d={path2}
          stroke={`var(${strokeColor})`}
          strokeWidth={strokeWidth}
        />
        <path
          d={path2}
          stroke={`var(${accentColor})`}
          strokeWidth={strokeWidth}
          id='progress'
        />

        <ProgressValueSC
          x='50%'
          y='50%'
          dominantBaseline='central'
          textAnchor='middle'
          fill={`var(${textColor})`}
          font={font}
        >
          {timer}
        </ProgressValueSC>
      </CounterSC>
    </CounterWrapper>
  ) : (
    <></>
  )
}
